import type { RefObject } from 'react'
import { useEffect, useState } from 'react'

// Default width of the indicators column, shared with its column config.
export const INDICATORS_COLUMN_DEFAULT_SIZE = 120

// Dots shown at the default column width.
const BASE_VISIBLE = 3
// Ceiling on the reveal, matching the tags column's cap.
const MAX_VISIBLE = 7
// Cell width beyond which the cell starts revealing more than BASE_VISIBLE dots.
// Set to the column's default size so a resting column stays a compact cluster
// and dots only appear once the user drags the column wider.
const EXPAND_MIN_WIDTH = INDICATORS_COLUMN_DEFAULT_SIZE
// Extra cell width that reveals one more dot. Dots are only ~12px each, so a
// pure fit calculation would surface them all at once; this paces the reveal to
// the drag distance instead.
const WIDTH_PER_EXTRA_DOT = 20

export const fitVisibleCount = (width: number, total: number): number => {
  const base = Math.min(total, BASE_VISIBLE)
  if (total <= BASE_VISIBLE || width <= EXPAND_MIN_WIDTH) return base

  const extra = Math.floor((width - EXPAND_MIN_WIDTH) / WIDTH_PER_EXTRA_DOT)
  return Math.min(total, MAX_VISIBLE, base + extra)
}

export const useVisibleDotCount = (
  containerRef: RefObject<HTMLElement | null>,
  totalTags: number,
  initialWidth: number,
): number => {
  const [visibleCount, setVisibleCount] = useState(() =>
    fitVisibleCount(initialWidth, totalTags),
  )

  useEffect(() => {
    const element = containerRef.current
    // Width only affects the cell once there are dots to overflow, so most
    // cells never pay for an observer.
    if (!element || totalTags <= BASE_VISIBLE) return

    // Storing the derived count rather than the raw width lets setState bail
    // out on unchanged values, so a column drag only re-renders the cell when
    // it crosses a reveal threshold instead of on every pixel.
    const update = (width: number) => {
      if (width > 0) setVisibleCount(fitVisibleCount(width, totalTags))
    }

    update(element.getBoundingClientRect().width)

    // Border-box, matching the seed and the initial read. contentRect drops
    // the container's padding, flickering a dot on the first resize.
    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (!entry) return

      const width =
        entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width
      if (width !== undefined) update(width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [containerRef, totalTags])

  return visibleCount
}
